import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLocalizationContext } from '@amzn/react-arb-tools';
import { ReportActionSource } from '@amzn/handshake-painter-typescript-client/v1';
import styled from '@emotion/styled';
import checkCircleFilledTokens from '@amzn/meridian-tokens/base/icon/check-circle-filled';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { ExternalLink } from 'src/common/externalLink';
import { useAnalyticsContext } from 'src/common/metrics';
import { BodyText, ImageBlock, QuantityBadge, SectionDivider } from 'src/common/styled';
import { usePageContext } from 'src/hooks';
import { formatWeekdayDate } from 'src/utils/date';
import { RetailRoutes, useRetailRedirectUrls } from 'src/utils/links';
import { MarketplaceIds } from 'src/utils/marketplace';
import { sendCloseMessage } from 'src/utils/messages';
import { getMessageOrFallback } from '../../utils';
// Figma's image tile; Rio has no 76px size
const IMAGE_TILE_SIZE = '76px';
const HeaderBar = styled(Row) `
  box-sizing: border-box;
  min-height: ${({ theme }) => `calc(${theme.rio.base.size[44]} + ${theme.rio.semantic.size.space.large})`};
  padding: ${({ theme }) => `${theme.rio.semantic.size.space.large} ${theme.rio.semantic.size.space.extraLarge} 0`};
`;
// Meridian ships this icon only at 24px; Figma's is 18px
const SuccessIcon = styled.div `
  display: flex;
  flex-shrink: 0;
  width: ${({ theme }) => theme.rio.base.size[20]};
  height: ${({ theme }) => theme.rio.base.size[18]};
  color: ${({ theme }) => theme.rio.base.color.green[400]};

  & > span {
    transform: scale(0.75);
    transform-origin: top left;
  }
`;
const Heading = styled(Text) `
  && {
    font-size: ${({ theme }) => theme.rio.semantic.size.font.large};
    line-height: ${({ theme }) => theme.rio.semantic.size.lineHeight.large};
    color: ${({ theme }) => theme.rio.base.color.green[400]};
  }
`;
// Lines the 12px icon, not the 32px button, up with the inset
const CloseSlot = styled.div `
  position: relative;
  left: ${({ theme }) => theme.rio.semantic.size.space.medium};
`;
const ConfirmationLine = styled(BodyText) `
  padding: ${({ theme }) => `0 ${theme.rio.semantic.size.space.extraLarge} ${theme.rio.semantic.size.space.large}`};
`;
const Summary = styled(Column) `
  padding: ${({ theme }) => theme.rio.semantic.size.space.large}
    ${({ theme }) => theme.rio.semantic.size.space.extraLarge};
  padding-bottom: ${({ theme }) => theme.rio.semantic.size.space.extraLarge};
`;
const ImageTile = styled(ImageBlock) `
  flex-shrink: 0;
  box-sizing: border-box;
  width: ${IMAGE_TILE_SIZE};
  height: ${IMAGE_TILE_SIZE};
  padding: ${({ theme }) => theme.rio.base.size[10]};
  justify-content: center;
  border-radius: ${({ theme }) => theme.rio.semantic.size.radius.small};
  background-color: ${({ theme }) => theme.rio.base.color.gray[1500]};

  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
  }
`;
/** The order placed view of the 1-Click sheet (Figma 2.3): TYP's confirmation for the verified order. */
export const OrderPlacedView = ({ item, shippingAddress, productImageUrl }) => {
    const { bundle, checkoutBundles } = usePageContext();
    const thankYouBundle = checkoutBundles.thankYouPage;
    const { localizationContext } = useLocalizationContext();
    const rumClient = useAnalyticsContext();
    const redirectUrls = useRetailRedirectUrls([RetailRoutes.ORDER_HISTORY]);
    const deliveryDate = formatWeekdayDate(item.estimatedArrival, localizationContext.getLocale());
    const isOutsideUS = (shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.country) !== MarketplaceIds.US;
    const onClose = () => {
        rumClient.recordEvent('ONE_CLICK_CONFIRMATION_CLOSE', { reason: 'close' });
        sendCloseMessage(rumClient);
    };
    return (_jsxs(Column, { spacing: "none", "data-testid": "one-click-order-placed", children: [_jsxs(HeaderBar, { spacing: "400", widths: ['fill', 'fit'], alignmentVertical: "center", children: [_jsxs(Row, { spacing: "300", alignmentVertical: "center", children: [_jsx(SuccessIcon, { children: _jsx(Icon, { tokens: checkCircleFilledTokens }) }), _jsx(Heading, { "data-font": "display", "data-testid": "one-click-order-placed-heading", children: thankYouBundle.getMessage('orderPlaced') })] }), _jsx(CloseSlot, { children: _jsx(Button, { type: "icon", size: "small", "aria-label": bundle.getMessage('close'), onClick: onClose, "data-testid": "one-click-close", children: _jsx(Icon, { tokens: closeSmallTokens }) }) })] }), _jsx(ConfirmationLine, { children: thankYouBundle.getMessage('confirmation') }), _jsx(SectionDivider, { subtle: true }), _jsxs(Summary, { spacing: "350", children: [_jsxs(Row, { spacing: "400", widths: ['fill', 'fit'], alignmentVertical: "center", children: [_jsxs(Column, { spacing: "350", children: [_jsxs("div", { "data-testid": "one-click-shipping", children: [_jsx(BodyText, { children: _jsx("b", { children: thankYouBundle.formatMessage((shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.isPickupLocation) ? 'shippingToPickup' : 'shippingToCustomer', { name: shippingAddress === null || shippingAddress === void 0 ? void 0 : shippingAddress.name }) }) }), shippingAddress && (_jsxs(BodyText, { children: [shippingAddress.addressLine1, shippingAddress.addressLine1 && _jsx("br", {}), shippingAddress.city, ", ", shippingAddress.zipCode, isOutsideUS && _jsx("br", {}), isOutsideUS && shippingAddress.country] }))] }), _jsx(SectionDivider, { subtle: true }), _jsx(Column, { spacing: "none", "data-testid": "one-click-estimated-delivery", children: getMessageOrFallback(() => (_jsxs(_Fragment, { children: [_jsx(BodyText, { children: _jsx("b", { children: deliveryDate }) }), _jsx(BodyText, { children: bundle.getMessage('estimatedDeliveryLabel') })] })), () => (_jsx(BodyText, { children: _jsx("b", { children: thankYouBundle.formatMessage('estimatedDelivery', { date: deliveryDate }) }) }))) })] }), _jsxs(ImageTile, { spacing: "none", children: [_jsx("img", { src: productImageUrl, alt: item.title, "data-testid": "one-click-placed-image" }), _jsx(QuantityBadge, { alignmentHorizontal: "center", width: "24px", height: "24px", children: _jsx(Text, { type: "b100", children: item.quantity }) })] })] }), _jsx(BodyText, { children: _jsx(ExternalLink, { href: redirectUrls.ORDER_HISTORY, id: "order-history-link", source: ReportActionSource.OneClickConfirmationSheet, children: thankYouBundle.getMessage('reviewAndEditOrders') }) })] })] }));
};
